import type { StockMovement, StockMovementReason } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { formatDate } from "../lib/format";
import { Spinner } from "./Spinner";
import { ErrorBanner } from "./ErrorBanner";

const REASON_META: Record<StockMovementReason, { label: string; className: string }> = {
  initial: { label: "Initial", className: "badge badge--muted" },
  receive: { label: "Receive", className: "badge badge--success" },
  return: { label: "Return", className: "badge badge--success" },
  sell: { label: "Sell", className: "badge badge--warning" },
  adjust: { label: "Adjust", className: "badge badge--muted" },
  correction: { label: "Correction", className: "badge badge--muted" },
};

interface ProductMovementsProps {
  productId: string;
  /** Bump this to force a reload after a stock adjustment. */
  reloadToken: number;
}

export function ProductMovements({ productId, reloadToken }: ProductMovementsProps) {
  const api = useApi();
  // reloadToken is in deps so the parent can refresh the trail after an adjust.
  const { data, loading, error, reload } = useAsync<StockMovement[]>(
    () => api.listMovements(productId),
    [api, productId, reloadToken],
  );

  if (loading) return <Spinner label="Loading movements…" />;
  if (error) return <ErrorBanner message={error.message} onRetry={reload} />;

  const movements = data ?? [];
  if (movements.length === 0) {
    return <p className="muted">No stock movements recorded yet.</p>;
  }

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Reason</th>
          <th>Change</th>
          <th>Resulting qty</th>
          <th>By</th>
          <th>Note</th>
        </tr>
      </thead>
      <tbody>
        {movements.map((m) => {
          const meta = REASON_META[m.reason];
          return (
            <tr key={m.id}>
              <td>{formatDate(m.createdAt)}</td>
              <td>
                <span className={meta.className}>{meta.label}</span>
              </td>
              <td>{m.delta > 0 ? `+${m.delta}` : m.delta}</td>
              <td>{m.quantityAfter}</td>
              <td>{m.createdBy ?? "—"}</td>
              <td>{m.note ?? "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
